import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetPdfTemplate,
  getGetPdfTemplateQueryKey,
  useSavePdfTemplate,
  useGeneratePdfTemplateArtwork,
  useUploadPdfTemplateLogo,
  type PdfArtworkPair,
} from "@workspace/api-client-react";
import { defaultPdfTemplate, pdfTemplateDesignSchema, type PdfTemplateDesign } from "@workspace/api-zod/pdf-template";
import { useAuth } from "@/hooks/use-auth";
import { clearDesignDraft, isPreviewAssetFailure, readDesignDraft, recoverPreviewAssets, writeDesignDraft } from "./pdf-design-recovery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { PdfTitleFontPicker } from "./pdf-title-font-picker";
import { PdfLogoPreview } from "./pdf-logo-preview";
import { cn } from "@/lib/utils";
import {
  AlertCircle,
  Check,
  Download,
  ImageOff,
  Loader2,
  PlugZap,
  RefreshCw,
  RotateCcw,
  Save,
  Sparkles,
  Trash2,
  Undo2,
  Upload,
  X,
} from "lucide-react";
import {
  apiErrorMessage,
  designProblems,
  designsEqual,
  HEX,
  MAX_LOGO_BYTES,
  readFileAsDataUrl,
  renderPreviewPdf,
} from "./pdf-template-utils";
import { PdfDocumentPreview } from "./pdf-document-preview";
import { ArtworkAllowance, useArtworkAllowanceRefresh } from "./pdf-artwork-allowance";

const LAYOUTS: { id: PdfTemplateDesign["layout"]; name: string; blurb: string }[] = [
  { id: "classic", name: "Classic", blurb: "Calm header band, familiar and tidy." },
  { id: "architect", name: "Architect", blurb: "Thin rules, generous white space." },
  { id: "bold", name: "Bold", blurb: "Full-colour blocks that stand out." },
];

function Section({ step, title, hint, children }: { step: string; title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-b border-border/70 pb-5 last:border-0">
      <div className="flex items-baseline gap-2.5">
        <span className="font-mono text-[10px] font-semibold tracking-widest text-primary">{step}</span>
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      </div>
      {hint && <p className="-mt-1.5 text-xs text-muted-foreground">{hint}</p>}
      {children}
    </section>
  );
}

function LayoutGlyph({ id, primary, accent }: { id: PdfTemplateDesign["layout"]; primary: string; accent: string }) {
  return (
    <svg viewBox="0 0 40 52" className="h-14 w-11 rounded-sm bg-background shadow-sm ring-1 ring-border" aria-hidden="true">
      {id === "classic" && <><rect x="0" y="0" width="40" height="9" fill={primary} /><rect x="5" y="15" width="18" height="2" fill={accent} /></>}
      {id === "architect" && <><rect x="5" y="6" width="30" height="0.8" fill={primary} /><rect x="5" y="10" width="12" height="2" fill={primary} /><rect x="5" y="46" width="30" height="0.8" fill={accent} /></>}
      {id === "bold" && <><rect x="0" y="0" width="40" height="22" fill={primary} /><rect x="5" y="16" width="16" height="3" fill={accent} /></>}
      {[26, 30, 34, 38].map((y) => <rect key={y} x="5" y={y} width={y === 38 ? 18 : 30} height="1.4" fill="currentColor" opacity="0.18" />)}
    </svg>
  );
}

function ArtworkStyleGlyph({ style, primary, accent }: { style: NonNullable<PdfTemplateDesign["artworkStyle"]>; primary: string; accent: string }) {
  return (
    <svg viewBox="0 0 40 52" className="h-14 w-11 rounded-sm bg-background shadow-sm ring-1 ring-border" aria-hidden="true" data-testid={`artwork-style-preview-${style}`}>
      {style === "modern" && <>
        <rect width="40" height="52" fill="#edf2f4" />
        <path d="M0 0H40V17L0 39Z" fill={primary} />
        <path d="M40 22V52H7Z" fill={accent} opacity=".8" />
        <path d="M5 39L30 24M5 44L24 33" stroke="#ffffff" strokeWidth="1" />
      </>}
      {style === "luxury" && <>
        <rect width="40" height="52" fill="#202832" />
        <path d="M0 52L40 9V52Z" fill={primary} opacity=".25" />
        <rect x="4" y="4" width="32" height="44" rx="1" fill="none" stroke="#c9ac72" strokeWidth=".6" />
        <path d="M9 35L31 13M9 40L31 18" stroke="#c9ac72" strokeWidth=".5" />
        <rect x="9" y="12" width="15" height="2" fill={accent} />
      </>}
      {style === "minimalist" && <>
        <rect width="40" height="52" fill="#faf8f2" />
        <path d="M8 43V27L20 18L32 27V43M14 43V30H26V43M5 43H35M20 18V13" fill="none" stroke={primary} strokeWidth=".7" />
        <path d="M8 8H23M8 11H17" stroke={accent} strokeWidth=".8" />
      </>}
    </svg>
  );
}

function ColorField({ id, label, value, onChange }: { id: string; label: string; value: string; onChange: (v: string) => void }) {
  const valid = HEX.test(value);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`input-${id}`} className="text-xs">{label}</Label>
      <div className={cn("flex items-center gap-2 rounded-md border bg-background pl-1.5 pr-2", !valid && "border-destructive")}>
        <input
          type="color"
          aria-label={`${label} picker`}
          value={valid ? value : "#000000"}
          onChange={(e) => onChange(e.target.value)}
          className="h-7 w-7 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
          data-testid={`picker-${id}`}
        />
        <input
          id={`input-${id}`}
          value={value}
          maxLength={7}
          onChange={(e) => onChange(e.target.value.startsWith("#") ? e.target.value : `#${e.target.value}`)}
          className="h-9 w-full bg-transparent font-mono text-sm uppercase outline-none"
          data-testid={`input-${id}`}
        />
      </div>
    </div>
  );
}

function CountedText({ id, label, value, max, onChange, multiline }: { id: string; label: string; value: string; max: number; onChange: (v: string) => void; multiline?: boolean }) {
  const near = value.length > max * 0.9;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={`input-${id}`} className="text-xs">{label}</Label>
        <span className={cn("font-mono text-[10px] tabular-nums", near ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground")}>{value.length}/{max}</span>
      </div>
      {multiline ? (
        <Textarea id={`input-${id}`} value={value} maxLength={max} rows={4} onChange={(e) => onChange(e.target.value)} data-testid={`input-${id}`} />
      ) : (
        <Input id={`input-${id}`} value={value} maxLength={max} onChange={(e) => onChange(e.target.value)} data-testid={`input-${id}`} />
      )}
    </div>
  );
}

export function PdfTemplateEditor({ onClose }: { onClose?: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { user, isLoading: authLoading } = useAuth();
  const queryKey = [...getGetPdfTemplateQueryKey(), user?.id];
  const query = useGetPdfTemplate({ query: { queryKey, enabled: !!user?.id, refetchInterval: 30_000, refetchOnWindowFocus: true, refetchOnMount: "always" } });
  const state = query.data;
  useArtworkAllowanceRefresh(state?.ai, query.refetch);

  const [draft, setDraft] = useState<PdfTemplateDesign | null>(null);
  const [baseline, setBaseline] = useState<PdfTemplateDesign | null>(null);
  const initialised = useRef<string | null>(null);
  const [recovered, setRecovered] = useState(false);

  // Initialise local draft once. Later refetches (e.g. after artwork
  // generation invalidates the quota) must never overwrite edits.
  useEffect(() => {
    if (!state || !user?.id || initialised.current === user.id || query.isFetching) return;
    initialised.current = user.id;
    const start = (state.design as PdfTemplateDesign | null) ?? defaultPdfTemplate(state.profile.brandColor, state.profile.logoUrl);
    let copy = null;
    try { copy = readDesignDraft(user.id, window.localStorage); } catch { /* Browser storage can be disabled. */ }
    setDraft(upgradeDesign(copy?.design ?? start));
    setRecovered(!!copy && !designsEqual(copy.design, start));
    setBaseline(start);
  }, [state, query.isFetching, user?.id]);

  const save = useSavePdfTemplate();
  const upload = useUploadPdfTemplateLogo();
  const artwork = useGeneratePdfTemplateArtwork();

  const [pending, setPending] = useState<Partial<PdfArtworkPair> | null>(null);
  const [brief, setBrief] = useState("");
  const [logoError, setLogoError] = useState<string | null>(null);
  const [artError, setArtError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const previewSeq = useRef(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const dirty = !!draft && !!baseline && (!designsEqual(draft, baseline) || !state?.design);
  useEffect(() => {
    if (!user?.id || initialised.current !== user.id || !draft) return;
    try {
      if (dirty) writeDesignDraft(user.id, draft, state?.version ?? null, window.localStorage);
      else clearDesignDraft(user.id, window.localStorage);
    } catch { /* Server saving remains available without browser storage. */ }
  }, [draft, dirty, user?.id, state?.version]);
  const problems = useMemo(() => (draft ? designProblems(draft) : []), [draft]);
  const update = (patch: Partial<PdfTemplateDesign>) => setDraft((d) => (d ? { ...upgradeDesign(d), ...patch } : d));

  const profileRef = useRef(state?.profile);
  profileRef.current = state?.profile;

  const runPreview = async (design: PdfTemplateDesign) => {
    const profile = profileRef.current;
    if (!profile || designProblems(design).length) return;
    const seq = ++previewSeq.current;
    setPreviewBusy(true);
    setPreviewError(null);
    const found: string[] = [];
    try {
      const blob = await renderPreviewPdf(design, profile, (m) => found.push(m));
      if (seq !== previewSeq.current) return;
      setPreviewBlob(blob);
      setWarnings(found);
    } catch (e) {
      if (seq === previewSeq.current) setPreviewError(apiErrorMessage(e, "The preview could not be generated."));
    } finally {
      if (seq === previewSeq.current) setPreviewBusy(false);
    }
  };
  const runPreviewRef = useRef(runPreview);
  runPreviewRef.current = runPreview;

  const draftKey = draft ? JSON.stringify(draft) : "";
  useEffect(() => {
    ++previewSeq.current;
    if (!draftKey) return;
    setPreviewBlob(null);
    setPreviewError(null);
    if (designProblems(JSON.parse(draftKey)).length) { setPreviewBusy(false); return; }
    setPreviewBusy(true);
    const t = window.setTimeout(() => runPreviewRef.current(JSON.parse(draftKey)), 800);
    return () => { window.clearTimeout(t); ++previewSeq.current; };
  }, [draftKey]);

  useEffect(() => {
    if (!previewBlob) { setPreviewUrl(null); return; }
    const url = URL.createObjectURL(previewBlob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [previewBlob]);

  if (query.isLoading || authLoading || (state && initialised.current !== user?.id)) {
    return (
      <div className="grid h-full gap-6 p-6 md:grid-cols-[380px_1fr]" data-testid="pdf-template-loading">
        <div className="space-y-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
        <Skeleton className="h-full min-h-[320px] w-full" />
      </div>
    );
  }
  if (!state) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-10 text-center" data-testid="pdf-template-error">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="font-medium">Your document design could not be loaded.</p>
        <p className="text-sm text-muted-foreground">{apiErrorMessage(query.error, "Please check your connection and try again.")}</p>
        <Button variant="outline" onClick={() => query.refetch()} data-testid="button-retry-pdf-template">Try again</Button>
      </div>
    );
  }
  if (!draft) return null;

  const { profile, ai } = state;

  const handleSave = () => {
    setSaveError(null);
    const submitted = draft;
    const owner = user?.id;
    save.mutate({ data: { design: submitted } }, {
      onSuccess: (res) => {
        if (initialised.current !== owner) return;
        const result = pdfTemplateDesignSchema.safeParse(res.design);
        if (!res.version || !result.success || !designsEqual(result.data, pdfTemplateDesignSchema.parse(submitted))) {
          setSaveError("The server did not confirm this design. Your changes are still here; please try saving again.");
          return;
        }
        const saved = result.data;
        // Do not erase edits made while the save request was in flight.
        setDraft(current => current && designsEqual(current, submitted) ? saved : current);
        setBaseline(saved);
        setRecovered(false);
        qc.setQueryData(queryKey, res);
        void qc.invalidateQueries({ queryKey: getGetPdfTemplateQueryKey() });
        toast({ title: "Design saved", description: "New quotes will use it. Existing quotes are unchanged." });
      },
      onError: (e) => setSaveError(apiErrorMessage(e, "Your design could not be saved.")),
    });
  };

  const handleDiscard = () => { if (baseline) setDraft(baseline); setRecovered(false); setPending(null); setSaveError(null); };
  const handleReset = () => { setDraft(defaultPdfTemplate(profile.brandColor, profile.logoUrl)); setPending(null); };

  const handleFile = async (file: File | undefined) => {
    setLogoError(null);
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) { setLogoError("Please choose a PNG or JPEG image."); return; }
    if (file.size > MAX_LOGO_BYTES) { setLogoError("That image is larger than 5 MB. Please choose a smaller file."); return; }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      upload.mutate({ data: { dataUrl } }, {
        onSuccess: (res) => update({ logoUrl: res.logoUrl }),
        onError: (e) => setLogoError(apiErrorMessage(e, "The logo could not be uploaded.")),
      });
    } catch (e) {
      setLogoError(apiErrorMessage(e, "Could not read that file."));
    }
  };

  const handleGenerate = (mode: "custom" | "automatic" = "custom") => {
    setArtError(null);
    artwork.mutate({ data: { mode, brief: mode === "automatic" ? undefined : brief.trim(),
      primaryColor: draft.primaryColor, accentColor: draft.accentColor,
      artworkStyle: draft.artworkStyle ?? "modern", logoUrl: draft.logoUrl } }, {
      onSuccess: (pair) => setPending(pair),
      onError: (e) => setArtError(apiErrorMessage(e, "Artwork could not be generated. Once started, failed attempts count. Your saved design is unchanged.")),
      onSettled: () => { void qc.invalidateQueries({ queryKey: getGetPdfTemplateQueryKey() }); },
    });
  };

  const acceptArt = (which: "cover" | "thankYou") => {
    if (!pending) return;
    if (which === "cover" && pending.coverArtworkUrl) update({ coverArtworkUrl: pending.coverArtworkUrl });
    if (which === "thankYou" && pending.thankYouArtworkUrl) update({ thankYouArtworkUrl: pending.thankYouArtworkUrl });
    dropArt(which);
  };
  const dropArt = (which: "cover" | "thankYou") => setPending((p) => {
    if (!p) return p;
    const next = { ...p, [which === "cover" ? "coverArtworkUrl" : "thankYouArtworkUrl"]: undefined };
    return next.coverArtworkUrl || next.thankYouArtworkUrl ? next : null;
  });

  const briefOk = brief.trim().length >= 10;
  const usingSettingsLogo = !!profile.logoUrl && draft.logoUrl === profile.logoUrl;

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="pdf-template-editor">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-card/60 px-4 py-3 sm:px-6">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold tracking-tight sm:text-lg">Quote document design</h2>
            {dirty ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:text-amber-300" data-testid="status-unsaved">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-500" />Unsaved changes
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-medium text-foreground" data-testid="status-saved">
                <Check className="h-3 w-3 text-primary" />Saved
              </span>
            )}
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">Saving sets the default for future quotes. Quotes you have already made keep their current look until you apply it on that quote.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="ghost" size="sm" onClick={handleReset} data-testid="button-reset-template"><RotateCcw className="mr-1.5 h-3.5 w-3.5" />Reset to default</Button>
          <Button variant="outline" size="sm" onClick={handleDiscard} disabled={!dirty || !state.design} data-testid="button-discard-template"><Undo2 className="mr-1.5 h-3.5 w-3.5" />Discard</Button>
          <Button size="sm" onClick={handleSave} disabled={!dirty || problems.length > 0 || save.isPending} data-testid="button-save-template">
            {save.isPending ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}Save design
          </Button>
          {onClose && <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close editor" data-testid="button-close-template"><X className="h-4 w-4" /></Button>}
        </div>
      </div>

      {recovered && dirty && (
        <p role="status" className="border-b bg-primary/5 px-6 py-2 text-xs" data-testid="text-restored-design-draft">
          Unsaved changes were recovered from this browser. Save design to keep them for future quotes, or Discard to return to your saved design.
        </p>
      )}
      {(saveError || problems.length > 0) && (
        <div className="border-b border-destructive/30 bg-destructive/10 px-6 py-2 text-xs text-destructive" data-testid="text-template-problems">
          {saveError ?? problems.join(" ")}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto md:grid md:grid-cols-[minmax(320px,400px)_1fr] md:overflow-hidden">
        {/* Controls */}
        <div className="min-h-0 shrink-0 space-y-5 px-4 py-5 sm:px-6 md:overflow-y-auto">
          <Section step="01" title="Layout">
            {baseline && state.design && baseline.pageVersion !== 6 && (
              <div className="rounded-md border border-primary/25 bg-primary/5 p-3 text-xs" data-testid="notice-full-page-upgrade">
                <p>{draft.pageVersion === 6 ? "Solid panels and a cover without prices ready to preview. Save to use them for future quotes. Existing quotes stay unchanged." : "This saved design uses the previous artwork or layout treatment."}</p>
                {draft.pageVersion !== 6 && <Button size="sm" variant="outline" className="mt-2" onClick={() => setDraft(upgradeDesign(draft))} data-testid="button-full-page-layout">Use updated design</Button>}
              </div>
            )}
            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Layout">
              {LAYOUTS.map((l) => {
                const on = draft.layout === l.id;
                return (
                  <button
                    key={l.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => update({ layout: l.id })}
                    className={cn("group flex flex-col items-center gap-2 rounded-lg border p-2.5 text-center transition-all hover:-translate-y-0.5", on ? "border-primary bg-primary/10 ring-1 ring-primary" : "bg-background hover:border-primary/50")}
                    data-testid={`button-layout-${l.id}`}
                  >
                    <LayoutGlyph id={l.id} primary={HEX.test(draft.primaryColor) ? draft.primaryColor : "#374349"} accent={HEX.test(draft.accentColor) ? draft.accentColor : "#5aabbf"} />
                    <span className="text-xs font-semibold">{l.name}</span>
                    <span className="hidden text-[10px] leading-tight text-muted-foreground sm:block">{l.blurb}</span>
                  </button>
                );
              })}
            </div>
          </Section>

          <PdfTitleFontPicker value={draft.titleFont} onChange={(titleFont) => update({ titleFont })} />

          <Section step="02" title="Colours" hint="Main colour controls icons and decoration. Text colour controls branded headings and price highlights independently. Other quote text keeps its original readable colour.">
            <div className="grid grid-cols-2 gap-3">
              <ColorField id="primary-color" label="Main colour" value={draft.primaryColor} onChange={(v) => update({ primaryColor: v })} />
              <ColorField id="accent-color" label="Accent colour" value={draft.accentColor} onChange={(v) => update({ accentColor: v })} />
            </div>
            <ColorField id="text-color" label="Text colour" value={draft.textColor ?? draft.primaryColor} onChange={(v) => update({ textColor: v })} />
            {draft.textColor ? (
              <button type="button" className="text-xs text-primary underline-offset-2 hover:underline" onClick={() => update({ textColor: undefined })} data-testid="button-link-text-color">Use main colour for text</button>
            ) : (
              <p className="text-xs text-muted-foreground">Text colour follows your main colour until you choose a separate colour.</p>
            )}
            {profile.brandColor && HEX.test(profile.brandColor) && draft.primaryColor.toLowerCase() !== profile.brandColor.toLowerCase() && (
              <button type="button" className="text-xs text-primary underline-offset-2 hover:underline" onClick={() => update({ primaryColor: profile.brandColor })} data-testid="button-use-brand-color">
                Use my brand colour ({profile.brandColor})
              </button>
            )}
          </Section>

          <Section step="03" title="Logo" hint="PNG or JPEG, up to 5 MB.">
            <div className="flex items-center gap-3 rounded-lg border bg-background p-3">
              <div className="flex h-14 w-24 shrink-0 items-center justify-center overflow-hidden rounded bg-muted/60">
                {draft.logoUrl ? <PdfLogoPreview url={draft.logoUrl} removeBackground={draft.removeLogoBackground ?? false} /> : <ImageOff className="h-5 w-5 text-muted-foreground" />}
              </div>
              <div className="min-w-0 flex-1 text-xs text-muted-foreground" data-testid="text-logo-source">
                {!draft.logoUrl ? "No logo. Your company name will be shown." : usingSettingsLogo ? "Using the logo from your company settings." : "Using an uploaded logo."}
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="input-logo-file" />
            <div className="flex items-center gap-1" data-testid="logo-actions">
              <Button variant="outline" size="sm" className="gap-1 px-2 text-xs" onClick={() => fileRef.current?.click()} disabled={upload.isPending} data-testid="button-upload-logo">
                {upload.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}Upload logo
              </Button>
              {profile.logoUrl && !usingSettingsLogo && (
                <Button variant="ghost" size="sm" className="px-2 text-xs" onClick={() => update({ logoUrl: profile.logoUrl })} data-testid="button-use-settings-logo">Use settings logo</Button>
              )}
              {draft.logoUrl && (
                <Button variant="ghost" size="sm" className="gap-1 px-2 text-xs" onClick={() => update({ logoUrl: null })} data-testid="button-remove-logo"><Trash2 className="h-3.5 w-3.5" />Remove</Button>
              )}
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Checkbox id="remove-logo-background" checked={draft.removeLogoBackground ?? false} onCheckedChange={(checked) => update({ removeLogoBackground: checked === true, hideLogoBackground: false })} data-testid="checkbox-remove-logo-background" />
                <Label htmlFor="remove-logo-background" className="cursor-pointer text-xs">Remove logo background</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="show-company-name" checked={draft.showCompanyName ?? profile.showCompanyName ?? true} onCheckedChange={(checked) => update({ showCompanyName: checked === true })} data-testid="checkbox-show-company-name" />
                <Label htmlFor="show-company-name" className="cursor-pointer text-xs">Show company name</Label>
              </div>
            </div>
            {logoError && <p className="text-xs text-destructive" data-testid="text-logo-error">{logoError}</p>}
          </Section>

          <Section step="04" title="Pages">
            <div className="space-y-2">
              {([
                ["coverEnabled", "Cover page", "A front page with your title and artwork."],
                ["thankYouEnabled", "Thank-you page", "A closing page with a personal note."],
                ["useInteriorBranding", "Use design colours on quote pages", "Turn off to keep original teal icons and price highlights on the inside pages."],
              ] as const).map(([key, label, desc]) => (
                <label key={key} className="flex cursor-pointer items-center justify-between gap-3 rounded-lg border bg-background px-3 py-2.5">
                  <span>
                    <span className="block text-sm font-medium">{label}</span>
                    <span className="block text-xs text-muted-foreground">{desc}</span>
                  </span>
                  <Switch checked={draft[key]} onCheckedChange={(v) => update({ [key]: v } as Partial<PdfTemplateDesign>)} data-testid={`switch-${key}`} />
                </label>
              ))}
            </div>
          </Section>

          {draft.coverEnabled && (
            <Section step="05" title="Cover page wording">
              <CountedText id="cover-title" label="Title" value={draft.coverTitle} max={90} onChange={(v) => update({ coverTitle: v })} />
              <CountedText id="cover-subtitle" label="Subtitle" value={draft.coverSubtitle} max={240} onChange={(v) => update({ coverSubtitle: v })} />
            </Section>
          )}

          {draft.thankYouEnabled && (
            <Section step={draft.coverEnabled ? "06" : "05"} title="Thank-you page wording">
              <CountedText id="thank-you-title" label="Title" value={draft.thankYouTitle} max={90} onChange={(v) => update({ thankYouTitle: v })} />
              <CountedText id="thank-you-message" label="Message" value={draft.thankYouMessage} max={900} onChange={(v) => update({ thankYouMessage: v })} multiline />
            </Section>
          )}

          <Section step="AI" title="Artwork" hint="Optional background images for the cover and thank-you pages.">
            <fieldset className="space-y-3" data-testid="artwork-style-picker">
              <legend className="text-sm font-semibold">Artwork style</legend>
              <p className="text-xs text-muted-foreground">Choose the style of your next AI-generated images. This does not change the PDF layout, fonts or existing artwork. Applies to both Create artwork and Generate for Me.</p>
              <div className="grid grid-cols-3 gap-2">
                {([
                  ["modern", "Modern", "Clean lines and geometry"],
                  ["minimalist", "Minimalist", "Fine linework and open space"],
                  ["luxury", "Luxury", "Refined materials and light"],
                ] as const).map(([value, label, hint]) => (
                  <label key={value} className={cn("group relative flex cursor-pointer flex-col items-center gap-2 rounded-lg border p-2.5 text-center transition-all hover:-translate-y-0.5 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-primary", (draft.artworkStyle ?? "modern") === value ? "border-primary bg-primary/10 ring-1 ring-primary" : "bg-background hover:border-primary/50")}>
                    <input type="radio" name="artwork-style" aria-label={label} className="sr-only" value={value} checked={(draft.artworkStyle ?? "modern") === value} onChange={() => update({ artworkStyle: value })} data-testid={`radio-artwork-style-${value}`} />
                    <ArtworkStyleGlyph style={value} primary={HEX.test(draft.primaryColor) ? draft.primaryColor : "#374349"} accent={HEX.test(draft.accentColor) ? draft.accentColor : "#5aabbf"} />
                    <span className="text-xs font-semibold">{label}</span>
                    <span className="hidden text-[10px] leading-tight text-muted-foreground sm:block">{hint}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            {(["coverArtworkUrl", "thankYouArtworkUrl"] as const).map((k) => draft[k] && (
              <div key={k} className="flex items-center gap-3 rounded-lg border bg-background p-2" data-testid={`artwork-current-${k}`}>
                <img src={draft[k]!} alt="" className="h-12 w-9 rounded object-cover" />
                <span className="flex-1 text-xs">{k === "coverArtworkUrl" ? "Cover artwork in use" : "Thank-you artwork in use"}</span>
                <Button variant="ghost" size="sm" onClick={() => update({ [k]: null } as Partial<PdfTemplateDesign>)} data-testid={`button-remove-${k}`}><Trash2 className="h-3.5 w-3.5" /><span className="sr-only">Remove</span></Button>
              </div>
            ))}

            {!ai.available ? (
              <div className="flex gap-3 rounded-lg border border-dashed bg-muted/40 p-3 text-xs" data-testid="notice-ai-unavailable">
                <PlugZap className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <div>
                  <p className="font-medium text-foreground">Artwork generation needs a connection</p>
                  <p className="mt-0.5 text-muted-foreground">The image service has not been connected yet. Everything else here works as normal.</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Textarea
                  value={brief}
                  maxLength={600}
                  rows={3}
                  placeholder="e.g. Soft architectural line drawing of a brick house extension, warm morning light"
                  onChange={(e) => setBrief(e.target.value)}
                  data-testid="input-artwork-brief"
                />
                <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2" data-testid="artwork-generation-options">
                  <Button size="sm" variant="outline" className="w-full min-w-0 gap-1 px-1 text-[11px] sm:text-xs" onClick={() => handleGenerate("custom")} disabled={!briefOk || artwork.isPending || (!ai.unlimited && (ai.remaining ?? 0) <= 0) || !!ai.blockedReason || query.isError || !!pending} aria-describedby="artwork-allowance-help" data-testid="button-generate-artwork">
                    {artwork.isPending ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 shrink-0" />}
                    {artwork.isPending ? "Creating…" : "Create artwork"}
                  </Button>
                  <span className="text-xs text-muted-foreground">or</span>
                  <Button size="sm" variant="outline" className="w-full min-w-0 gap-1 px-1 text-[11px] sm:text-xs" onClick={() => handleGenerate("automatic")} disabled={artwork.isPending || (!ai.unlimited && (ai.remaining ?? 0) <= 0) || !!ai.blockedReason || query.isError || !!pending} aria-describedby="artwork-allowance-help" data-testid="button-generate-artwork-automatic">
                    {artwork.isPending ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 shrink-0" />}Generate for Me
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground" data-testid="text-artwork-remaining">{ai.unlimited ? "Unlimited artwork attempts" : <>{ai.remaining} of {ai.limit} artwork attempts available</>}</p>
                <ArtworkAllowance ai={ai} />
                {query.isRefetchError && <p className="text-xs text-destructive">Could not refresh artwork availability. <button type="button" className="underline" onClick={() => query.refetch()}>Check again</button></p>}
                {pending && <p className="text-[11px] text-muted-foreground">Keep or discard the new images below before creating more.</p>}
              </div>
            )}
            {artError && <p className="text-xs text-destructive" data-testid="text-artwork-error">{artError}</p>}

            {pending && (
              <div className="grid grid-cols-2 gap-3" data-testid="artwork-pending">
                {(["cover", "thankYou"] as const).map((w) => {
                  const url = w === "cover" ? pending.coverArtworkUrl : pending.thankYouArtworkUrl;
                  if (!url) return <div key={w} />;
                  return (
                    <div key={w} className="animate-in fade-in zoom-in-95 overflow-hidden rounded-lg border bg-background">
                      <img src={url} alt={w === "cover" ? "New cover artwork" : "New thank-you artwork"} className="aspect-[3/4] w-full object-cover" />
                      <div className="space-y-1.5 p-2">
                        <p className="text-[11px] font-medium">{w === "cover" ? "Cover" : "Thank-you"}</p>
                        <div className="flex gap-1.5">
                          <Button size="sm" className="h-7 flex-1 px-2 text-xs" onClick={() => acceptArt(w)} data-testid={`button-accept-${w}-artwork`}>Use</Button>
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => dropArt(w)} data-testid={`button-discard-${w}-artwork`}>Discard</Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>
        </div>

        {/* Preview */}
        <div className="relative flex min-h-[520px] min-w-0 shrink-0 flex-col border-t bg-[hsl(200_14%_25%/0.06)] md:min-h-0 md:border-l md:border-t-0">
          <div className="flex items-center justify-between gap-2 border-b bg-background/70 px-4 py-2 backdrop-blur">
            <div className="flex items-center gap-2 text-xs">
              <span className="font-mono uppercase tracking-widest text-muted-foreground">Sample PDF</span>
              {previewBusy && <Loader2 className="h-3 w-3 animate-spin text-primary" data-testid="status-preview-busy" />}
            </div>
            <div className="flex gap-1.5">
              <Button variant="ghost" size="sm" onClick={() => runPreview(draft)} disabled={previewBusy || problems.length > 0} data-testid="button-refresh-preview"><RefreshCw className="mr-1.5 h-3.5 w-3.5" />Refresh</Button>
              {previewUrl ? (
                <Button variant="outline" size="sm" asChild>
                  <a href={previewUrl} download="sample-quote-preview.pdf" data-testid="link-download-preview"><Download className="mr-1.5 h-3.5 w-3.5" />Download</a>
                </Button>
              ) : (
                <Button variant="outline" size="sm" disabled data-testid="link-download-preview"><Download className="mr-1.5 h-3.5 w-3.5" />Download</Button>
              )}
            </div>
          </div>
          {warnings.length > 0 && (
            <div className="border-b border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs text-amber-800 dark:text-amber-200" data-testid="text-preview-warnings">{warnings.join(" ")}</div>
          )}
          <div className="relative max-h-[70dvh] min-h-0 flex-1 overflow-auto p-3 sm:p-5 md:max-h-none" role="region" aria-label="Sample PDF pages" tabIndex={0}>
            {previewError ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 text-center" data-testid="text-preview-error">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <p className="text-sm">{previewError}</p>
                <Button size="sm" variant="outline" onClick={() => runPreview(draft)}>Try again</Button>
              </div>
            ) : previewBlob ? (
              <PdfDocumentPreview blob={previewBlob} onRetry={() => runPreview(draft)} onAssetError={error => {
                if (!isPreviewAssetFailure(error) || save.isPending || !user?.id) return;
                try {
                  recoverPreviewAssets(user.id, draft, state.version ?? null,
                    import.meta.env.VITE_SENTRY_RELEASE || "development", window.localStorage, window.sessionStorage,
                    () => window.location.reload());
                } catch { /* Never reload if recovery storage is unavailable. */ }
              }} />
            ) : (
              <div className="mx-auto flex h-full max-w-sm flex-col gap-3 rounded-md bg-background p-6 shadow-sm" data-testid="preview-skeleton">
                <Skeleton className="h-16 w-full" /><Skeleton className="h-4 w-2/3" /><Skeleton className="h-4 w-1/2" /><Skeleton className="mt-6 h-40 w-full" />
              </div>
            )}
          </div>
          <p className="px-4 pb-3 text-[11px] text-muted-foreground">Sample customer and prices only. Previewing never saves a quote.</p>
        </div>
      </div>
    </div>
  );
}

function upgradeDesign(design: PdfTemplateDesign): PdfTemplateDesign {
  // Preserve explicit opt-outs in main-colour designs; earlier designs defaulted branding off.
  return { ...design, companyNameSize: design.companyNameSize ?? 16, companyNameFont: "title", logoAlignment: design.logoAlignment ?? "left", pageVersion: 6, ...(design.hideLogoBackground ? { removeLogoBackground: design.removeLogoBackground ?? true, hideLogoBackground: false } : {}), useInteriorBranding: (design.pageVersion ?? 0) >= 3 ? design.useInteriorBranding : true };
}
